"use client"
import React, { memo, useEffect, useRef, useState, useCallback } from 'react'
import HUD from './HUD';
import { airtableFields } from './airtable';
import { ChunkAST } from '../lib/parseMarkdown';
import Markdown, { FootnoteRef } from './Markdown';

// NEW: Import panelData so we can derive our default chart data directly.
import { panelData, PanelRow } from '@/panelData';
import AudioPlayer from './AudioPlayer';
import { useTooltip } from '@/components/Tooltip';

// Define an interface for the date marker that includes the parsed chart data.
interface DateMarker {
  offset: number;
  header: string;
  dataPoint: PanelRow; // Ideally this should be ChartDataPoint, but if importing types is circular, you can use any
  childOfThisSection: boolean;
  ref: HTMLDivElement;
}

// Add any extra "report/details" you want in your chunk
export interface ChunkWithMeta {
  chunk: ChunkAST;
  title: string;
  reportDetails?: {
    [key in typeof airtableFields[number]]: string
  };
}

type Props = {
  contentSections: ChunkWithMeta[];
  gridAreas?: { content: string; toc: string; graphgap: string; graph: string };
  topElement?: React.ReactNode;
  bottomElement?: React.ReactNode;
  name?: string;
  startingIndex?: number;
  className?: string;
};

// Extract a memoized component for each Markdown section
const SectionMarkdown = memo(function SectionMarkdown({
  nodes,
  docContext,
  ending
}: {
  nodes: ChunkAST['nodes'];
  docContext: ChunkAST['docContext'];
  ending?: 'slowdown' | 'race'
}) {
  return <Markdown nodes={nodes} docContext={docContext} ending={ending} FootnoteHandler={FootnoteRef} />;
});

const TooltipComponent = ({scrollPosition, children}: {scrollPosition: number, children: React.ReactElement[]}) => {
  const {ref, getReferenceProps, getFloatingProps, floatingRef, floatingStyles, open} = useTooltip({placement: "bottom-end", interactive: true});
  if ((scrollPosition * 100) > 2) return <div className="graph-border-box md:sticky top-5 overflow-hidden">{children}</div>;
  return <>
    <div ref={ref} {...getReferenceProps()} className={`graph-border-box md:sticky top-5 overflow-hidden`}>
      {children}
    </div>
    {open && <div ref={floatingRef} {...getFloatingProps()} style={floatingStyles} className="rounded-lg p-4 text-xs font-mono border-[1.5px] border-black max-w-[300px]">
      <div >
        This set of charts shows you important statistics as the scenario progresses. It changes as you scroll down.
      </div>
    </div>}
  </>
}

export default function ArticleContent({
  contentSections,
  gridAreas = { content: 'content', toc: 'toc', graphgap: 'graphgap', graph: 'graph' },
  topElement,
  bottomElement,
  name = 'narrative',
  startingIndex = 0,
  className
}: Props) {

  // Initialize the current chart date based on the default data.
  const [currentChartDate, setCurrentChartDate] = useState<Date>(
    new Date(new Date(panelData[0][0]).getTime())
  );
  const [dateMarkers, setDateMarkers] = useState<DateMarker[]>([]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const contentSectionRef = useRef<HTMLDivElement | null>(null);
  // Refs for each <div> chunk for scroll logic:
  const sectionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const tocRef = useRef<HTMLDivElement | null>(null);

  // Use a stable callback if possible for the ref assignments.
  const setSectionRef = useCallback((el: HTMLDivElement | null, index: number) => {
    sectionRefs.current[index] = el;
  }, []);

  const setContentSectionRef = useCallback((el: HTMLDivElement | null) => {
    contentSectionRef.current = el;
  }, []);

  // Scroll logic for highlighting the current chunk
  // Check if we're on a mobile device
  useEffect(() => {
    const checkMobile = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth <= 930);
      }
    };

    // Initial check
    checkMobile();

    // Listen for resize events
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!sectionRefs.current || sectionRefs.current.length === 0) return;

    const scrollListener = () => {
      if (contentSectionRef.current && tocRef.current) {
        // Compute scroll progress for the Table of Contents:
        const elTop = contentSectionRef.current.offsetTop;
        const elHeight = contentSectionRef.current.offsetHeight;
        const progress = (window.scrollY + window.innerHeight / 2 - elTop) / elHeight;
        const clampedProgress = Math.max(0, Math.min(progress, 1));
        setScrollPosition(clampedProgress);
        tocRef.current.style.setProperty("--scrollPosition", `${clampedProgress}`);
      }

      const allDateMarkerElements = document.querySelectorAll('[data-chart-date]');
      if (!allDateMarkerElements || allDateMarkerElements.length === 0) return;

      let currentDate: string | null = null;
      allDateMarkerElements.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight / 2) {
          // The last date element passing the midpoint will set our current date.
          currentDate = el.getAttribute("data-chart-date");
        }
      });

      const childrenDateMarkerElements = Array.from(contentSectionRef.current?.querySelectorAll('[data-chart-date]') || []);

      setDateMarkers(
        Array.from(allDateMarkerElements).flatMap((el): DateMarker[] => {
          const offset =
            ((el as HTMLDivElement).offsetTop - (contentSectionRef.current?.offsetTop || 0)) /
            (contentSectionRef.current?.offsetHeight || 1);
          const header = el.getAttribute("data-chart-date-header") || "";
          const extraAttr = el.getAttribute("data-chart-extra");
          let dataPoint = null;
          if (extraAttr) {
            try {
              dataPoint = JSON.parse(extraAttr);
              return [{ offset, header, dataPoint, childOfThisSection: childrenDateMarkerElements.includes(el), ref: el as HTMLDivElement }]
            } catch (e) {
              console.error("Error parsing chart data from date marker", e);
            }
          }
          return [];
        })
      );
      if (currentDate) {
        setCurrentChartDate(new Date(new Date(currentDate).getTime() + 24 * 60 * 60 * 1000));
      }
    };

    scrollListener();
    document.addEventListener("scroll", scrollListener);
    return () => {
      document.removeEventListener("scroll", scrollListener);
    };
  }, [contentSections, startingIndex]);

  

  return (
    <>
      {/* Mobile HUD (for PIP mode) */}
      {isMobile && (
        <HUD
          currentDate={currentChartDate}
          defaultChartData={panelData}
          chartData={dateMarkers.map(marker => marker.dataPoint)}
        />
      )}

      {/* Table of contents on the left */}
      <div ref={tocRef}
        className={`pt-[44px] z-10 hidden lg:block`}
        style={{ gridArea: gridAreas.toc }}
      >
        <nav className="section-nav sticky left-2 top-2 group opacity">
          <div className="sm:flex flex-col h-[100vh] pb-[26px] hidden">
            <svg width="130" style={{ height: 'calc(100vh - 4px)', opacity: (scrollPosition * 100) > 2 ? 1 : 0 }} fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute top-0 transition-opacity duration-1000">
              {/* <circle cx="4" cy="4" r="3" fill={index + 1 <= currentSectionIndex ? "currentColor" : "transparent"} /> */}
              <line x1="3" y1="8" x2="3" y2={`${Math.max(scrollPosition * 100, 2)}%`} stroke="currentColor" strokeWidth="2" />
              {dateMarkers.map(({ offset, header, childOfThisSection, dataPoint }, index) => <>
                {childOfThisSection && <svg key={index} x={0} y={`${offset * 97}%`}>
                  {/* <span
                    className="text-black text-xs pl-[1px]"xp
                    style={{
                      background: 'linear-gradient(to bottom, transparent 20%, var(--background) 30%, var(--background) 70%, transparent 80%)'
                    }}
                  >
                    {(index + 1) <= currentSectionIndex ? "●" : "○"}
                  </span> */}
                  {header && <><circle cx="3" cy="6" r="3" fill={'black'} />
                    <circle cx="3" cy="6" r="2" fill={offset <= scrollPosition ? "transparent" : "white"} />
                    <text
                      href={`#${name}-${dataPoint[0]}`}
                      fill="currentColor"
                      x={12}
                      y={11}
                      width={200}
                      height={20}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer min-[1601px]:opacity-100"
                    >
                      <a className="text-black no-underline" href={`#${name}-${dataPoint[0]}`}>{header}</a>
                    </text></>}
                </svg>}
              </>)}
            </svg>
          </div>
        </nav>
      </div>

      {/* Main content */}
      <section
        ref={setContentSectionRef}
        className={`main-content ${className}`}
        style={{ gridArea: gridAreas.content }}
      >
        {topElement}
        <div>
          {contentSections.map((sectionMeta, index) => (
            <div
              key={`section-${name}-${index}`}
              id={`section-${name}-${index}`}
              className="scroll-mt-[54vh]"
              ref={(el) => setSectionRef(el, index)}
            >
              <SectionMarkdown
                nodes={sectionMeta.chunk.nodes}
                docContext={["slowdown", "race"].includes(name) ? { ...sectionMeta.chunk.docContext, prefix: name } : sectionMeta.chunk.docContext}
                ending={name === 'slowdown' ? 'slowdown' : (name === 'race' ? 'race' : undefined)}
              />
            </div>
          ))}
        </div>
        {bottomElement}
      </section>

      {/* Gap area */}
      <div
        className="hidden md:block"
        style={{ gridArea: gridAreas.graphgap }}
      />

      {/* Sticky chart on the right - only displayed on desktop */}
      {gridAreas.graph === "graph" && !isMobile && (
        <div
          className={`group justify-self-start transition-width duration-700 pt-9 hidden md:block w-[430px]`}
          style={{ gridArea: gridAreas.graph }}
        >
          <TooltipComponent scrollPosition={scrollPosition}>
            
              <div className={``}>
                <HUD
                  currentDate={currentChartDate}
                  defaultChartData={panelData}
                  chartData={dateMarkers.map(marker => marker.dataPoint)}
                />
              </div>
              
              <AudioPlayer />
          </TooltipComponent>
        </div>

      )}
    </>
  );
}